import { useEffect, useState } from 'react';
import {
  Stack,
  Title,
  TextInput,
  Loader,
  Center,
  Text,
  Table,
  Badge,
  Group,
  Select,
  Button,
} from '@mantine/core';
import { Search, RefreshCw } from 'lucide-react';
import { useStore } from '../store/useStore';
import { Device, DeviceState } from '../types';
import { DeviceDetailDrawer } from '../components/DeviceDetailDrawer';

function getDeviceStateBadge(state: DeviceState) {
  const colors: Record<DeviceState, string> = {
    [DeviceState.PENDING]: 'yellow',
    [DeviceState.CONFIGURED]: 'blue',
    [DeviceState.ACTIVE]: 'green',
    [DeviceState.INACTIVE]: 'gray',
    [DeviceState.DISABLED]: 'red',
  };

  return <Badge color={colors[state]}>{state.toUpperCase()}</Badge>;
}

function formatTimestamp(timestamp?: string) {
  if (!timestamp) return 'Never';
  const date = new Date(timestamp);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
  return `${Math.floor(diffMins / 1440)}d ago`;
}

export function Devices() {
  const fetchDevices = useStore((state) => state.fetchDevices);
  const devices = useStore((state) => state.devices);
  const devicesLoading = useStore((state) => state.devicesLoading);

  const [searchQuery, setSearchQuery] = useState('');
  const [stateFilter, setStateFilter] = useState<string | null>(null);
  const [selectedDevice, setSelectedDevice] = useState<Device | null>(null);
  const [drawerOpened, setDrawerOpened] = useState(false);

  useEffect(() => {
    fetchDevices();
  }, [fetchDevices]);

  const handleRefresh = () => {
    fetchDevices({
      state: stateFilter as DeviceState | undefined,
      search: searchQuery || undefined,
    });
  };

  const handleDeviceClick = (device: Device) => {
    setSelectedDevice(device);
    setDrawerOpened(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpened(false);
    handleRefresh();
  };

  // Filter devices based on search and filters
  const filteredDevices = devices.filter((device) => {
    if (searchQuery && !device.mac_address?.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    if (stateFilter && device.state !== stateFilter) {
      return false;
    }
    return true;
  });

  const pendingCount = devices.filter((d) => d.state === DeviceState.PENDING).length;

  return (
    <Stack gap="lg">
      <Group justify="space-between">
        <div>
          <Title order={1}>Devices</Title>
          {pendingCount > 0 && (
            <Text size="sm" c="dimmed" mt={4}>
              {pendingCount} device{pendingCount !== 1 ? 's' : ''} pending
            </Text>
          )}
        </div>
        <Button
          leftSection={<RefreshCw size={16} />}
          onClick={handleRefresh}
          loading={devicesLoading}
        >
          Refresh
        </Button>
      </Group>

      <Group>
        <TextInput
          placeholder="Search by MAC address..."
          leftSection={<Search size={16} />}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.currentTarget.value)}
          style={{ flex: 1 }}
        />
        <Select
          placeholder="Filter by state"
          data={[
            { value: '', label: 'All States' },
            { value: DeviceState.PENDING, label: 'Pending' },
            { value: DeviceState.CONFIGURED, label: 'Configured' },
            { value: DeviceState.ACTIVE, label: 'Active' },
            { value: DeviceState.INACTIVE, label: 'Inactive' },
            { value: DeviceState.DISABLED, label: 'Disabled' },
          ]}
          value={stateFilter}
          onChange={setStateFilter}
          clearable
          style={{ width: 200 }}
        />
      </Group>

      {devicesLoading ? (
        <Center h={400}>
          <Loader size="lg" />
        </Center>
      ) : filteredDevices.length > 0 ? (
        <Table highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>MAC Address</Table.Th>
              <Table.Th>Sensor Name</Table.Th>
              <Table.Th>State</Table.Th>
              <Table.Th>Location</Table.Th>
              <Table.Th>Hardware</Table.Th>
              <Table.Th>Last Heartbeat</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filteredDevices.map((device) => (
              <Table.Tr
                key={device.sensor_id}
                onClick={() => handleDeviceClick(device)}
                style={{ cursor: 'pointer' }}
              >
                <Table.Td>
                  <Text size="sm" ff="monospace">
                    {device.mac_address}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" component="span">
                    {device.sensor_name || <Text c="dimmed" component="span">—</Text>}
                  </Text>
                </Table.Td>
                <Table.Td>{getDeviceStateBadge(device.state)}</Table.Td>
                <Table.Td>
                  <Text size="sm" component="span">
                    {device.location ? (
                      `${device.location.lat.toFixed(4)}, ${device.location.lon.toFixed(4)}`
                    ) : (
                      <Text c="dimmed" component="span">No location</Text>
                    )}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="xs" c="dimmed">
                    {device.hardware_info?.hardware_model || 'Unknown'}
                  </Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{formatTimestamp(device.last_heartbeat)}</Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      ) : (
        <Center h={400}>
          <Text c="dimmed">No devices found</Text>
        </Center>
      )}

      {selectedDevice && (
        <DeviceDetailDrawer
          device={selectedDevice}
          opened={drawerOpened}
          onClose={handleDrawerClose}
        />
      )}
    </Stack>
  );
}
